import React from 'react';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { Redirect, Route, RoutesPaths, RoutesProvider, Switch, useRouterHelper, RoutePath } from '@shared/core';
import { useGuestListController } from './GuestList.controller';
import Root from './routes/Root';
import Create from './routes/Create';
import Update from './routes/Update';

interface Props {
  eventId: string;
  eventHandle: string;
  firebaseEventId: string;
}

type GuestListRouteNames = 'root' | 'create' | 'update';

type GuestListRoutePaths = RoutesPaths<GuestListRouteNames>;

const guestListRoutePaths: GuestListRoutePaths = {
  root: {
    path: '',
    goToPath: () => ''
  },
  create: {
    path: 'create',
    goToPath: () => 'create'
  },
  update: {
    path: 'update/:contactId',
    goToPath: () => 'update/:contactId'
  }
};

const GuestListRoutes: React.FC<Props> = ({ eventId, eventHandle, firebaseEventId }) => {
  const routeHelpers = useRouterHelper();
  const isMobile = useIsMobileScreen();
  const { isLoading, contactCollectionId } = useGuestListController(firebaseEventId);

  if (isLoading || !isMobile) {
    return null;
  }

  const routes: Array<RoutePath<{ contactId: string }>> = [
    {
      path: routeHelpers.buildPath(guestListRoutePaths.root.path),
      component: () => <Root eventId={eventId} eventHandle={eventHandle} firebaseEventId={firebaseEventId} />,
      exact: true
    },
    {
      path: routeHelpers.buildPath(guestListRoutePaths.create.path),
      component: () =>
        contactCollectionId ? <Create eventId={eventId} eventHandle={eventHandle} firebaseEventId={firebaseEventId} contactCollectionId={contactCollectionId} /> : null
    },
    {
      path: routeHelpers.buildPath(guestListRoutePaths.update.path),
      component: ({ match }) =>
        match.params.contactId ? <Update eventId={eventId} eventHandle={eventHandle} firebaseEventId={firebaseEventId} contactId={match.params.contactId} /> : null
    }
  ];

  return (
    <Switch>
      {routes.map(route => (
        <Route key={route.path} path={route.path} render={route.component} exact={route.exact} />
      ))}
      <Redirect to={routes[0].path} />
    </Switch>
  );
};

const GuestList: React.FC<Props> = ({ eventId, eventHandle, firebaseEventId }) => (
  <RoutesProvider appUrl="guestlist" handle={eventHandle}>
    <GuestListRoutes eventId={eventId} eventHandle={eventHandle} firebaseEventId={firebaseEventId} />
  </RoutesProvider>
);

export default GuestList;
